/**
 * Where your list of places lives: localStorage, a JSON blob you can paste
 * around, and the URL fragment.
 *
 * All three are the same handful of fields, so they share one tolerant parser —
 * anything that arrives from outside the app (hand-edited JSON, a link someone
 * sent, storage written by an older build) is untrusted and gets validated the
 * same way. Nothing here touches the server: the fragment never leaves the
 * browser, and storage is per-device by definition.
 */

import type { Point } from './types';

const STORAGE_KEY = 'lowcation.places.v1';

/** Matches the weight slider in PointList. */
const MIN_WEIGHT = 1;
const MAX_WEIGHT = 10;

export interface SavedPlace {
  label: string;
  lat: number;
  lon: number;
  weight: number;
}

let seq = 0;
export const nextPointId = (): string => `p${++seq}`;

function coord(v: unknown): number | null {
  const n = typeof v === 'string' ? Number(v.trim()) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

function clampWeight(v: unknown): number {
  const n = coord(v);
  if (n === null) return 5;
  return Math.min(MAX_WEIGHT, Math.max(MIN_WEIGHT, Math.round(n)));
}

/**
 * Read a list of places from anything list-shaped.
 *
 * Deliberately forgiving about the wrapper (`[…]` or `{ places: […] }`) and the
 * spelling (`lon`/`lng`, `label`/`name`), because the export format is meant to
 * be edited by hand. Coordinates are the one hard requirement: a row without
 * usable ones is skipped rather than dropped in the middle of the ocean.
 */
export function parsePlaces(input: unknown): Point[] {
  const wrapper = input as { places?: unknown } | null;
  const list = Array.isArray(input) ? input : Array.isArray(wrapper?.places) ? wrapper.places : null;
  if (!list) throw new Error('expected a JSON array of places, or { "places": [ … ] }');

  const out: Point[] = [];
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const lat = coord(r.lat ?? r.latitude);
    const lon = coord(r.lon ?? r.lng ?? r.longitude);
    if (lat === null || lon === null || Math.abs(lat) > 90 || Math.abs(lon) > 180) continue;
    const label = String(r.label ?? r.name ?? '').trim();
    out.push({
      id: nextPointId(),
      lat,
      lon,
      label: label || `${lat.toFixed(4)}, ${lon.toFixed(4)}`,
      weight: clampWeight(r.weight),
    });
  }
  if (!out.length) throw new Error('no usable places found — each one needs a lat and a lon');
  return out;
}

const round = (n: number, places: number) => Number(n.toFixed(places));

export function toSaved(points: Point[]): SavedPlace[] {
  // Six decimals is ~10 cm; the pins are not that precise, and it keeps the
  // exported JSON readable.
  return points.map((p) => ({
    label: p.label,
    lat: round(p.lat, 6),
    lon: round(p.lon, 6),
    weight: p.weight,
  }));
}

export function exportJson(points: Point[]): string {
  return JSON.stringify({ app: 'lowcation', version: 1, places: toSaved(points) }, null, 2);
}

// ------------------------------------------------------------------ storage

export function savePlaces(points: Point[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSaved(points)));
  } catch {
    // Private mode, or storage full. Losing the list on reload is survivable;
    // breaking every edit is not.
  }
}

function loadStored(): Point[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? parsePlaces(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
}

// ------------------------------------------------------------------ the link

/**
 * One place per `;`, as `lat,lon,weight,label`.
 *
 * Chosen over base64'd JSON so the link stays legible and short: the whole
 * point of a fragment is that a human can see what they're about to open. The
 * label is percent-encoded, so neither separator can occur inside it.
 */
export function encodeHash(points: Point[]): string {
  return points
    .map((p) =>
      // Five decimals is ~1 m — plenty for a shared pin.
      [p.lat.toFixed(5), p.lon.toFixed(5), p.weight, encodeURIComponent(p.label)].join(',')
    )
    .join(';');
}

export function decodeHash(encoded: string): Point[] {
  const rows = encoded
    .split(';')
    .filter(Boolean)
    .map((chunk) => {
      const [lat, lon, weight, ...label] = chunk.split(',');
      return { lat, lon, weight, label: safeDecode(label.join(',')) };
    });
  return parsePlaces(rows);
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    // A stray '%' in a hand-mangled link shouldn't lose the whole place.
    return s;
  }
}

export function permalink(points: Point[]): string {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}#p=${encodeHash(points)}`;
}

/** Pull the places out of a full permalink, or of a bare fragment. */
export function placesFromLink(text: string): Point[] | null {
  const m = /(?:^|[#&])p=([^&\s]+)/.exec(text.trim());
  if (!m) return null;
  return decodeHash(m[1]);
}

/**
 * What the app starts with: a link's places if it was opened with one,
 * otherwise whatever this browser had last.
 *
 * Runs once at module load rather than in a state initialiser, because it has a
 * side effect — the fragment is consumed. Once adopted, the places are yours and
 * belong in storage, so the URL goes back to being clean and later edits don't
 * silently disagree with a link still sitting in the address bar.
 */
export function readInitialPlaces(): Point[] {
  try {
    const fromLink = placesFromLink(window.location.hash);
    if (fromLink) {
      savePlaces(fromLink);
      history.replaceState(null, '', window.location.pathname + window.location.search);
      return fromLink;
    }
  } catch {
    // A malformed link falls back to stored places rather than an empty app.
  }
  return loadStored();
}
